import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { EtatService } from '../../Services/etat';
import { ParcService } from '../../Services/parc';
import { InterventionActualisation } from '../../Services/intervention-actualisation';
import { filtresInterventionsMois, reunirInterventions } from '../../Services/interventions';
import { jourPlanification } from '../../Services/intervention-dates';
import {
  InterventionModalComponent,
  InterventionModalData,
} from '../intervention-modal/intervention-modal';
import {
  creerModalIntervention,
  MaintenanceIntervention,
  mapperIntervention,
  comparerInterventionsChronologiquement,
  formatTimeRange,
  trackIntervention,
  installationLabel,
  lienTicket,
} from './intervention-agenda';

@Component({
  selector: 'app-prochaines-interventions',
  imports: [RouterLink, InterventionModalComponent],
  templateUrl: './prochaines-interventions.html',
  styleUrl: './prochaines-interventions.css',
})
export class ProchainesInterventions {
  /** Maintenance fournit ses données ; sinon, charge les interventions à venir du parc. */
  readonly items = input<MaintenanceIntervention[]>();
  /** Sans mois explicite (page Tickets), aucune date de fin n’est imposée. */
  readonly month = input<Date>();
  readonly loading = input(false);
  readonly error = input('');
  readonly selectedKey = model<string | null>(null);
  private readonly etat = inject(EtatService);
  private readonly parcService = inject(ParcService);
  private readonly actualisation = inject(InterventionActualisation);
  private readonly destroyRef = inject(DestroyRef);
  private chargementId = 0;
  private readonly loadedItems = signal<MaintenanceIntervention[]>([]);
  private readonly pending = signal(false);
  private readonly failure = signal('');
  readonly chargementInterventions = computed(() =>
    this.items() === undefined ? this.pending() : this.loading(),
  );
  readonly erreurInterventions = computed(() =>
    this.items() === undefined ? this.failure() : this.error(),
  );
  readonly prochaines = computed(() =>
    (this.items() ?? this.loadedItems())
      .filter((item) => item.startsAt.slice(0, 10) >= jourPlanification())
      .sort(comparerInterventionsChronologiquement),
  );
  readonly upcomingGroups = computed(() => {
    const groups = new Map<string, MaintenanceIntervention[]>();
    for (const item of this.prochaines()) {
      const date = item.startsAt.slice(0, 10);
      groups.set(date, [...(groups.get(date) ?? []), item]);
    }
    return [...groups].map(([date, items]) => ({
      date,
      items,
      label: new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }),
    }));
  });
  readonly formatTimeRange = formatTimeRange;
  readonly trackIntervention = trackIntervention;
  readonly installationLabel = installationLabel;
  readonly lienTicket = lienTicket;
  /** Conserve le jour civil fourni par l’API, sans décalage lié au fuseau du navigateur. */
  formatDate(date: string, avecJour = false): string {
    const valeur = new Date(`${date.slice(0, 10)}T12:00:00`);
    if (!Number.isFinite(valeur.getTime())) return '';
    return valeur.toLocaleDateString('fr-FR', {
      ...(avecJour ? { weekday: 'short' as const } : {}),
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  readonly interventionOuverte = signal<MaintenanceIntervention | null>(null);
  readonly interventionModalData = computed<InterventionModalData | null>(() => {
    const intervention = this.interventionOuverte();
    if (!intervention) return null;

    return creerModalIntervention(intervention);
  });

  ouvrirInterventionModal(item: MaintenanceIntervention, event: Event) {
    if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus();
    this.selectedKey.set(trackIntervention(0, item));
    this.interventionOuverte.set(item);
  }

  constructor() {
    effect(() => {
      const items = this.items();
      const parc = this.etat.parcSelectionne();
      const month = this.month();
      this.actualisation.revision();
      const id = ++this.chargementId;
      this.interventionOuverte.set(null);
      if (items !== undefined) return;
      this.loadedItems.set([]);
      this.failure.set('');
      this.pending.set(false);
      this.selectedKey.set(null);
      if (parc) void this.charger(parc.id, month, id);
    });
  }

  /** Ignore les réponses d’un ancien parc/mois et celles reçues après destruction. */
  private async charger(parcId: number, month: Date | undefined, id: number) {
    const estValide = () =>
      !this.destroyRef.destroyed &&
      id === this.chargementId &&
      parcId === this.etat.parcSelectionne()?.id &&
      month === this.month() &&
      this.items() === undefined;
    this.pending.set(true);
    try {
      const response = await this.parcService.getParcInterventions(
        parcId,
        month ? filtresInterventionsMois(month) : { debut: `${jourPlanification()} 00:00:00` },
      );
      if (!estValide()) return;
      if (!response) throw new Error('Réponse interventions indisponible');
      this.loadedItems.set(reunirInterventions(response).map(mapperIntervention));
    } catch {
      if (estValide()) this.failure.set('Impossible de charger les interventions.');
    } finally {
      if (estValide()) this.pending.set(false);
    }
  }
}
